import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft, BriefcaseBusiness, CheckCircle2, ChevronRight, Eye, EyeOff,
  FileText, Loader2, MoreHorizontal, Plus, Sparkles, Trash2, TrendingUp, Video, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useClaudeAI } from '../hooks/useClaudeAI';
import { useDialog } from '../components/AppDialog';
import RocketLoader from '../components/RocketLoader';
import DailyLimitModal from '../components/DailyLimitModal';

// ─── helpers ─────────────────────────────────────────────────────────────────

const getJobEmoji = (title = '') => {
  const t = title.toLowerCase();
  if (t.match(/engineer|developer|programmer|software|frontend|backend|fullstack/)) return '💻';
  if (t.match(/design|ux|ui/)) return '🎨';
  if (t.match(/data|analyst|analytics|scientist/)) return '📊';
  if (t.match(/market|seo|growth/)) return '📈';
  if (t.match(/sales|account executive/)) return '💰';
  if (t.match(/product/)) return '🚀';
  if (t.match(/finance|accountant|financial/)) return '💼';
  if (t.match(/hr|human resource|recruiter|talent/)) return '👥';
  if (t.match(/research|scientist|lab/)) return '🔬';
  if (t.match(/health|medical|nurse|doctor|clinical/)) return '🏥';
  if (t.match(/teach|professor|educat|instructor/)) return '🎓';
  if (t.match(/writer|content|editor|journalist/)) return '✍️';
  if (t.match(/legal|lawyer|attorney/)) return '⚖️';
  if (t.match(/operations|director|manager|lead|chief|officer|head/)) return '📋';
  return '💼';
};

const renderBold = (text = '') => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((p, i) => i % 2 === 1 ? <strong key={i}>{p}</strong> : p);
};

const SENIORITIES = ['junior', 'mid', 'senior'];

// ─── sub-components ───────────────────────────────────────────────────────────

const AiBadge = () => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: 'linear-gradient(135deg,#FB923C,#F97316)', color: 'white', borderRadius: 6,
    fontSize: 11, fontWeight: 800, padding: '2px 7px', letterSpacing: 0.3,
  }}>
    AI
  </span>
);

const SENIORITY_COLORS = { junior: '#059669', mid: '#0891B2', senior: '#7C3AED' };

const scoreColor = (s) => (s >= 8 ? '#059669' : s >= 5 ? '#D97706' : '#EF4444');

const ScoreBadge = ({ score, size = 'sm' }) => {
  const c = scoreColor(score);
  return (
    <span style={{
      fontSize: size === 'sm' ? 10 : 13, fontWeight: 800, color: c, background: `${c}14`,
      border: `1px solid ${c}28`, borderRadius: 5, padding: size === 'sm' ? '1px 6px' : '3px 10px',
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {score}/10
    </span>
  );
};

const InterviewCard = ({ interview, onClick, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const emoji = getJobEmoji(interview.title);
  const qCount = interview.questions?.length || 0;
  const seniority = interview.seniority || 'mid';
  const seniorityColor = SENIORITY_COLORS[seniority] || '#6B7280';
  const dateStr = interview.createdAt
    ? new Date(interview.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  const isVideo = interview.mode === 'video';
  const isAI = interview.mode === 'personalized';
  const accentColor = isVideo ? '#EF1D28' : '#7C3AED'; // red for video, violet for text-based — matches the mode cards above
  const practiced = (interview.questions || []).filter(q => q.practice?.score != null);
  const avgScore = practiced.length
    ? Math.round(practiced.reduce((s, q) => s + q.practice.score, 0) / practiced.length * 10) / 10
    : null;

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  return (
    <div
      onClick={onClick}
      style={{
        background: 'white', border: '1px solid #EAECF0', borderRadius: 16,
        cursor: 'pointer', position: 'relative',
        transition: 'all 0.18s', boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        display: 'flex', flexDirection: 'column',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 10px 32px ${accentColor}20`; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = accentColor + '50'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = '#EAECF0'; }}
    >
      {/* Tinted header zone */}
      <div style={{
        background: `linear-gradient(135deg, ${accentColor}12 0%, ${accentColor}05 100%)`,
        padding: '16px 16px 14px',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10,
        borderBottom: `1px solid ${accentColor}10`,
        borderRadius: '14px 14px 0 0',
      }}>
        {/* Emoji avatar */}
        <div style={{
          width: 46, height: 46, borderRadius: 13, flexShrink: 0,
          background: 'white', border: `1.5px solid ${accentColor}25`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, boxShadow: `0 2px 8px ${accentColor}14`,
        }}>
          {emoji}
        </div>
        {/* Mode + seniority badges */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          {isVideo && (
            <span style={{ fontSize: 9, fontWeight: 800, color: '#C5161D', background: '#FFF1F2', border: '1px solid #FECACA', borderRadius: 4, padding: '2px 7px', letterSpacing: 0.3 }}>
              VIDEO
            </span>
          )}
          {isAI && !isVideo && (
            <span style={{ fontSize: 9, fontWeight: 800, color: '#6D28D9', background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 4, padding: '2px 7px', letterSpacing: 0.3 }}>
              AI
            </span>
          )}
          <span style={{ fontSize: 9, fontWeight: 800, color: seniorityColor, background: `${seniorityColor}14`, border: `1px solid ${seniorityColor}28`, borderRadius: 4, padding: '2px 7px', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {seniority}
          </span>
        </div>
      </div>

      {/* Title + footer */}
      <div style={{ padding: '12px 16px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', lineHeight: 1.4 }}>
          {interview.title}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {qCount > 0 && (
              <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 500 }}>
                {qCount} Q{qCount !== 1 ? 's' : ''}
              </span>
            )}
            {dateStr && <span style={{ fontSize: 11, color: '#E5E7EB' }}>·</span>}
            {dateStr && <span style={{ fontSize: 11, color: '#C0C9D8' }}>{dateStr}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {avgScore !== null && (
              <span style={{ fontSize: 10, fontWeight: 800, color: scoreColor(avgScore), background: `${scoreColor(avgScore)}12`, border: `1px solid ${scoreColor(avgScore)}25`, borderRadius: 5, padding: '1px 7px' }}>
                {avgScore}/10
              </span>
            )}
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button
                onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
                style={{ border: 'none', background: '#F3F4F6', cursor: 'pointer', color: '#9CA3AF', padding: '4px 6px', borderRadius: 6, display: 'flex', alignItems: 'center', transition: 'background 0.12s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#E5E7EB'}
                onMouseLeave={e => e.currentTarget.style.background = '#F3F4F6'}
              >
                <MoreHorizontal size={14} />
              </button>
              {menuOpen && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 200, background: 'white', border: '1px solid #E5E7EB', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', minWidth: 130, overflow: 'hidden' }}>
                  <button
                    onClick={e => { e.stopPropagation(); setMenuOpen(false); onDelete(interview._id); }}
                    style={{ width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', padding: '10px 14px', fontSize: 13, fontWeight: 600, color: '#EF4444', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#FFF1F2'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Create Modal ─────────────────────────────────────────────────────────────

const CreateModal = ({ onClose, onCreated, docs }) => {
  const [mode, setMode] = useState('basic');
  const [step, setStep] = useState(1);
  const [jobTitle, setJobTitle] = useState('');
  const [jobAd, setJobAd] = useState('');
  const [seniority, setSeniority] = useState('mid');
  const [selectedDocId, setSelectedDocId] = useState('');
  const { generateInterviewQuestions, loading, dailyLimitError, clearDailyLimitError } = useClaudeAI();
  const { showAlert } = useDialog();

  const resumeDocs = (docs || []).filter(d => d.type === 'resume' || d.type === 'cv');

  const handleGenerate = async () => {
    if (!jobTitle.trim()) return;
    const selectedDoc = resumeDocs.find(d => d._id === selectedDocId);
    const resumeData = selectedDoc?.data || null;
    try {
      const result = await generateInterviewQuestions({
        jobTitle: jobTitle.trim(),
        seniority,
        mode,
        jobAd: mode === 'personalized' ? jobAd : '',
        resumeData: mode === 'personalized' ? resumeData : null,
      });
      const interview = await api.createInterview({
        title: jobTitle.trim(),
        mode,
        seniority,
        jobAd: mode === 'personalized' ? jobAd : '',
        questions: result.questions || [],
      });
      onCreated(interview);
    } catch (err) {
      showAlert('Failed to generate questions: ' + err.message, { type: 'error', title: 'Generation Failed' });
    }
  };

  const tabBtn = (id, label) => (
    <button
      key={id}
      type="button"
      onClick={() => { setMode(id); setStep(1); }}
      style={{
        flex: 1, border: mode === id ? '1.5px solid #EF1D28' : '1.5px solid #E5E7EB',
        borderRadius: 8, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        background: mode === id ? 'transparent' : 'white',
        color: mode === id ? '#EF1D28' : '#6B7280',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}
    >
      {id === 'basic' ? '🕐' : '⊕'} {label}
    </button>
  );

  const seniorityBtn = (s) => (
    <button
      key={s}
      type="button"
      onClick={() => setSeniority(s)}
      style={{
        flex: 1, border: seniority === s ? '2px solid #111827' : '1.5px solid #E5E7EB',
        borderRadius: 8, padding: '9px 0', fontSize: 13, fontWeight: seniority === s ? 700 : 500,
        cursor: 'pointer', background: 'white', color: seniority === s ? '#111827' : '#6B7280',
        textTransform: 'capitalize',
      }}
    >
      {s.charAt(0).toUpperCase() + s.slice(1)}
    </button>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: 'white', borderRadius: 18, padding: '28px 28px 24px',
        width: '100%', maxWidth: 420, position: 'relative',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16, border: 'none', background: 'transparent',
            cursor: 'pointer', color: '#9CA3AF', padding: 4,
          }}
        >
          <X size={20} />
        </button>

        <h2 style={{ margin: '0 0 18px', fontSize: 20, fontWeight: 800, color: '#111827' }}>
          Job Interview Questions
        </h2>

        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {tabBtn('basic', 'Basic')}
          {tabBtn('personalized', 'Personalized')}
        </div>

        {/* Description */}
        <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6B7280', lineHeight: 1.55 }}>
          {mode === 'basic'
            ? 'In Basic mode, quickly generates a set of interview questions tailored to your desired job position.'
            : 'In Personalized mode, generates interview questions tailored not only to the job you want but also to your previous work experience.'}
        </p>

        {/* Step indicator for personalized */}
        {mode === 'personalized' && (
          <div style={{ textAlign: 'center', color: '#6B7280', fontSize: 12, fontWeight: 600, marginBottom: 16 }}>
            Step {step}/2
          </div>
        )}

        {/* Step 1 fields */}
        {step === 1 && (
          <>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Job title from the job ad <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              value={jobTitle}
              onChange={e => setJobTitle(e.target.value)}
              placeholder="e.g. Product Manager"
              style={{
                width: '100%', border: '1.5px solid #E5E7EB', borderRadius: 8,
                padding: '10px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box',
                marginBottom: 16,
              }}
              onFocus={e => { e.target.style.borderColor = '#EF1D28'; }}
              onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }}
            />

            {mode === 'personalized' && (
              <>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Copy-paste the job ad here <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <div style={{ position: 'relative', marginBottom: 6 }}>
                  <textarea
                    value={jobAd}
                    onChange={e => { if (e.target.value.length <= 6000) setJobAd(e.target.value); }}
                    placeholder="Paste the full job description here..."
                    rows={5}
                    style={{
                      width: '100%', border: '1.5px solid #E5E7EB', borderRadius: 8,
                      padding: '10px 12px', fontSize: 13, outline: 'none', resize: 'vertical',
                      boxSizing: 'border-box', fontFamily: 'inherit',
                    }}
                    onFocus={e => { e.target.style.borderColor = '#EF1D28'; }}
                    onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }}
                  />
                  <div style={{ textAlign: 'right', fontSize: 11, color: '#9CA3AF', marginBottom: 12 }}>
                    {jobAd.length}/6000
                  </div>
                </div>
              </>
            )}

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
              Seniority
            </label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
              {SENIORITIES.map(seniorityBtn)}
            </div>
          </>
        )}

        {/* Step 2: resume selection (personalized mode) */}
        {step === 2 && mode === 'personalized' && (
          <>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
              Select a resume to personalize questions (optional)
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 10, border: '1.5px solid',
                borderColor: !selectedDocId ? '#EF1D28' : '#E5E7EB',
                borderRadius: 9, padding: '10px 14px', cursor: 'pointer',
                background: !selectedDocId ? '#FFF1E8' : 'white',
              }}>
                <input type="radio" name="resumeDoc" value="" checked={!selectedDocId}
                  onChange={() => setSelectedDocId('')} style={{ accentColor: '#EF1D28' }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>No resume (use job ad only)</span>
              </label>
              {resumeDocs.map(doc => (
                <label key={doc._id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, border: '1.5px solid',
                  borderColor: selectedDocId === doc._id ? '#EF1D28' : '#E5E7EB',
                  borderRadius: 9, padding: '10px 14px', cursor: 'pointer',
                  background: selectedDocId === doc._id ? '#FFF1E8' : 'white',
                }}>
                  <input type="radio" name="resumeDoc" value={doc._id} checked={selectedDocId === doc._id}
                    onChange={() => setSelectedDocId(doc._id)} style={{ accentColor: '#EF1D28' }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                    {doc.title || 'Untitled'} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({doc.type})</span>
                  </span>
                </label>
              ))}
            </div>
          </>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          {mode === 'personalized' && step === 1 ? (
            <button
              type="button"
              disabled={!jobTitle.trim() || !jobAd.trim()}
              onClick={() => setStep(2)}
              style={{
                background: '#EF4444', color: 'white', border: 'none', borderRadius: 10,
                padding: '12px 28px', fontSize: 15, fontWeight: 700, cursor: !jobTitle.trim() || !jobAd.trim() ? 'not-allowed' : 'pointer',
                opacity: !jobTitle.trim() || !jobAd.trim() ? 0.5 : 1,
              }}
            >
              Next <ChevronRight size={16} style={{ display: 'inline', verticalAlign: 'middle' }} />
            </button>
          ) : (
            <button
              type="button"
              disabled={!jobTitle.trim() || loading}
              onClick={handleGenerate}
              style={{
                background: 'linear-gradient(135deg,#FB923C,#F97316)', color: 'white', border: 'none',
                borderRadius: 10, padding: '12px 24px', fontSize: 14, fontWeight: 700,
                cursor: !jobTitle.trim() || loading ? 'not-allowed' : 'pointer',
                opacity: !jobTitle.trim() ? 0.5 : 1,
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              {loading ? <Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Sparkles size={16} />}
              {loading ? 'Generating…' : 'Generate with AI'}
            </button>
          )}
        </div>
      </div>
      {dailyLimitError && <DailyLimitModal onClose={clearDailyLimitError} />}
    </div>
  );
};

// ─── Video Report View ────────────────────────────────────────────────────────

const VideoReportView = ({ interview }) => {
  const vr = interview.videoReport;
  const cm = interview.communicationMetrics;
  const questions = interview.questions || [];
  const duration = interview.duration;

  const likelyInfo = {
    strong:            { label: 'Strong Hire',     color: '#059669', bg: '#ECFDF5' },
    moderate:          { label: 'Moderate Fit',    color: '#D97706', bg: '#FFFBEB' },
    weak:              { label: 'Needs Work',      color: '#EA580C', bg: '#FFF7ED' },
    'not recommended': { label: 'Not Recommended', color: '#DC2626', bg: '#FEF2F2' },
  };
  const likely = likelyInfo[vr?.hiringLikelihood] || likelyInfo.moderate;

  const wi = (w) => w === 'high' ? { label: 'High',   color: '#EF4444', bg: '#FEF2F2' }
                  : w === 'low'  ? { label: 'Low',    color: '#6B7280', bg: '#F3F4F6' }
                  :                { label: 'Medium',  color: '#F59E0B', bg: '#FFFBEB' };

  const si = (s) => s >= 9 ? { color: '#8B5CF6', bg: '#F5F3FF' }
                  : s >= 6 ? { color: '#10B981', bg: '#ECFDF5' }
                  : s >= 3 ? { color: '#F59E0B', bg: '#FFFBEB' }
                  :          { color: '#EF4444', bg: '#FEF2F2' };

  const Bar = ({ label, value, color }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 12, color, fontWeight: 700 }}>{value}%</span>
      </div>
      <div style={{ height: 5, background: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${value}%`, background: color, borderRadius: 3 }} />
      </div>
    </div>
  );

  const totalScore = questions.reduce((s, q) => s + (q.practice?.score || 0), 0);
  const maxRaw = questions.length * 10;
  const answeredCount = questions.filter(q => q.practice?.userAnswer && q.practice.userAnswer !== '(No answer provided)').length;

  const fmtDur = (secs) => {
    if (!secs) return '';
    return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  };

  if (questions.length === 0 || answeredCount === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#9CA3AF', textAlign: 'center', padding: 24 }}>
        <div style={{ fontSize: 48 }}>🎙️</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>Session Ended Early</div>
        <div style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 300 }}>No answers were recorded in this session. Start a new video interview to generate a full report.</div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', overflowY: 'auto', paddingRight: 4 }}>
      {/* Summary */}
      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 14, padding: '20px 22px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <div style={{ width: 76, height: 76, borderRadius: '50%', background: '#F5F3FF', border: '3px solid #7C3AED', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: '#7C3AED', lineHeight: 1 }}>
              {vr ? vr.overallScore : (maxRaw > 0 ? Math.round(totalScore / maxRaw * 100) : 0)}
            </span>
            <span style={{ fontSize: 10, color: '#7C3AED', opacity: 0.75 }}>{vr ? '/100' : '%'}</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 3 }}>{interview.title}</div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>
              {questions.length} questions{duration ? ` · ${fmtDur(duration)}` : ''} · AI Video Interview
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {vr && <span style={{ fontSize: 11, fontWeight: 700, color: likely.color, background: likely.bg, borderRadius: 5, padding: '2px 10px', border: `1px solid ${likely.color}33` }}>{likely.label}</span>}
              <span style={{ fontSize: 11, fontWeight: 700, color: '#0891B2', background: '#ECFEFF', borderRadius: 5, padding: '2px 10px', border: '1px solid #A5F3FC' }}>
                {totalScore}/{maxRaw} pts
              </span>
            </div>
          </div>
        </div>
        {vr?.summary && (
          <p style={{ fontSize: 12, color: '#374151', lineHeight: 1.5, margin: '12px 0 0', fontStyle: 'italic', borderTop: '1px solid #F3F4F6', paddingTop: 12 }}>
            "{vr.summary}"
          </p>
        )}
      </div>

      {/* Performance + Highlights */}
      {vr && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 12, padding: '16px 18px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Performance</div>
            <Bar label="Technical"     value={vr.technicalScore}     color="#3B82F6" />
            <Bar label="Communication" value={vr.communicationScore} color="#8B5CF6" />
            <Bar label="Confidence"    value={vr.confidenceScore}    color="#10B981" />
            <Bar label="Behavioral"    value={vr.behavioralScore}    color="#F59E0B" />
          </div>
          <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 12, padding: '16px 18px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Highlights</div>
            {vr.strongestAnswer && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#059669', marginBottom: 4 }}>BEST ANSWER</div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.45 }}>{vr.strongestAnswer}</div>
              </div>
            )}
            {vr.weakestAnswer && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#DC2626', marginBottom: 4 }}>NEEDS WORK</div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.45 }}>{vr.weakestAnswer}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Strengths + Improvement areas */}
      {vr && (vr.strengths?.length > 0 || vr.improvementAreas?.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          {vr.strengths?.length > 0 && (
            <div style={{ background: '#F0FDF4', borderRadius: 12, padding: '14px 16px', border: '1px solid #BBF7D0' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#15803D', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Key Strengths</div>
              {vr.strengths.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5, alignItems: 'flex-start' }}>
                  <span style={{ color: '#15803D', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>✓</span>
                  <span style={{ fontSize: 12, color: '#166534', lineHeight: 1.4 }}>{s}</span>
                </div>
              ))}
            </div>
          )}
          {vr.improvementAreas?.length > 0 && (
            <div style={{ background: '#FFFBEB', borderRadius: 12, padding: '14px 16px', border: '1px solid #FDE68A' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#B45309', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Areas to Improve</div>
              {vr.improvementAreas.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5, alignItems: 'flex-start' }}>
                  <span style={{ color: '#B45309', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>→</span>
                  <span style={{ fontSize: 12, color: '#92400E', lineHeight: 1.4 }}>{a}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Communication metrics */}
      {cm && cm.answers > 0 && (
        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 12, padding: '14px 18px', marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Speaking Metrics</div>
          <div style={{ display: 'flex', gap: 0 }}>
            {[
              { label: 'Total Words',  value: cm.totalWords, color: '#3B82F6' },
              { label: 'Avg / Answer', value: Math.round(cm.totalWords / cm.answers), color: '#8B5CF6' },
              { label: 'Filler Words', value: cm.fillerWords, color: cm.fillerWords > 15 ? '#EF4444' : '#10B981' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ flex: 1, textAlign: 'center', padding: '4px 8px' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next steps */}
      {vr?.recommendedNextSteps?.length > 0 && (
        <div style={{ background: '#EFF6FF', borderRadius: 12, padding: '14px 16px', marginBottom: 14, border: '1px solid #BFDBFE' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#1E40AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Recommended Next Steps</div>
          {vr.recommendedNextSteps.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-start' }}>
              <span style={{ minWidth: 20, height: 20, borderRadius: '50%', background: '#2563EB', color: 'white', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
              <span style={{ fontSize: 12, color: '#1E40AF', lineHeight: 1.45 }}>{step}</span>
            </div>
          ))}
        </div>
      )}

      {/* Per-question cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {questions.map((q, i) => {
          const score = q.practice?.score ?? q.practice?.realtimeScore ?? null;
          const sInfo = si(score ?? 0);
          const wInfo = wi(q.practice?.weightage);
          const qLabel = q.type === 'clarify' ? 'Clarification' : q.type === 'followup' ? 'Follow-up' : 'Main Question';
          const noAnswer = !q.practice?.userAnswer || q.practice.userAnswer === '(No answer provided)';
          return (
            <div key={i} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>Q{i + 1}</span>
                  <span style={{
                    fontSize: 9, fontWeight: 700, borderRadius: 4, padding: '1px 6px',
                    color: (q.type === 'main' || q.type === 'interview') ? '#6B7280' : '#7C3AED',
                    background: (q.type === 'main' || q.type === 'interview') ? '#F3F4F6' : '#F5F3FF',
                  }}>{qLabel}</span>
                </div>
                <div style={{ display: 'flex', gap: 5 }}>
                  {q.practice?.weightage && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: wInfo.color, background: wInfo.bg, borderRadius: 4, padding: '1px 8px', border: `1px solid ${wInfo.color}33` }}>{wInfo.label}</span>
                  )}
                  {score !== null && (
                    <span style={{ fontSize: 11, fontWeight: 800, color: sInfo.color, background: sInfo.bg, borderRadius: 4, padding: '1px 9px', border: `1px solid ${sInfo.color}33` }}>{score}/10</span>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.5, marginBottom: 8 }}>{q.question}</div>
              <div style={{ background: '#F8F9FB', borderRadius: 7, padding: '8px 12px', marginBottom: 10, border: '1px solid #EAECF0' }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Your Answer</div>
                <div style={{ fontSize: 12, color: noAnswer ? '#9CA3AF' : '#374151', lineHeight: 1.5, fontStyle: noAnswer ? 'italic' : 'normal' }}>
                  {q.practice?.userAnswer || '(No answer provided)'}
                </div>
              </div>
              {(q.practice?.strengths?.length > 0 || q.practice?.improvements?.length > 0) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {q.practice?.strengths?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Strengths</div>
                      {q.practice.strengths.slice(0, 2).map((s, j) => (
                        <div key={j} style={{ fontSize: 11, color: '#374151', lineHeight: 1.4, marginBottom: 3 }}>+ {s}</div>
                      ))}
                    </div>
                  )}
                  {q.practice?.improvements?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#D97706', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Improve</div>
                      {q.practice.improvements.slice(0, 2).map((s, j) => (
                        <div key={j} style={{ fontSize: 11, color: '#374151', lineHeight: 1.4, marginBottom: 3 }}>→ {s}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Detail View ──────────────────────────────────────────────────────────────

const DetailView = ({ interview, onBack, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('interview');
  const [selectedQ, setSelectedQ] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [draftAnswer, setDraftAnswer] = useState('');
  const [saving, setSaving] = useState(false);
  const { evaluateInterviewAnswer, loading: aiLoading, dailyLimitError: detailDailyLimit, clearDailyLimitError: clearDetailDailyLimit } = useClaudeAI();
  const { showAlert } = useDialog();

  useEffect(() => { setSelectedQ(null); setShowAnswer(false); }, [activeTab]);

  const allQ = interview.questions || [];
  const questions = allQ.filter(q => q.type === activeTab);
  const found = selectedQ != null
    ? (allQ.find(item => item._id === selectedQ) || questions[selectedQ] || allQ[selectedQ])
    : null;

  /* Prefill the answer box with the saved practice answer when switching questions */
  useEffect(() => {
    const f = (interview.questions || []).find(item => item._id === selectedQ);
    setDraftAnswer(f?.practice?.userAnswer || '');
  }, [selectedQ, interview]);

  const practiced = allQ.filter(item => item.practice?.score != null);
  const avgScore = practiced.length
    ? Math.round((practiced.reduce((s, item) => s + item.practice.score, 0) / practiced.length) * 10) / 10
    : null;
  const weakest = [...practiced]
    .sort((a, b) => a.practice.score - b.practice.score)
    .slice(0, 3)
    .filter(item => item.practice.score < 8);

  const handleGetFeedback = async () => {
    const answer = draftAnswer.trim();
    if (!answer || !found?._id) return;
    try {
      const fb = await evaluateInterviewAnswer({
        question: found.question,
        hint: found.hint,
        userAnswer: answer,
        jobTitle: interview.title,
        seniority: interview.seniority || 'mid',
      });
      const updatedQuestions = allQ.map(item => item._id === found._id
        ? { ...item, practice: { userAnswer: answer, ...fb, practicedAt: new Date().toISOString() } }
        : item);
      setSaving(true);
      const updated = await api.updateInterview(interview._id, { questions: updatedQuestions });
      onUpdate?.(updated);
    } catch (e) {
      showAlert('Could not get AI feedback: ' + e.message, { type: 'error', title: 'AI Feedback' });
    } finally {
      setSaving(false);
    }
  };

  const seniority = interview.seniority || 'mid';
  const accentColor = SENIORITY_COLORS[seniority] || '#6B7280';

  return (
    <div className="interview-detail-split" style={{ display: 'flex', gap: 0, height: '100%' }}>
      {/* ── Left panel ── */}
      <div className="interview-detail-sidebar" style={{
        width: 300, flexShrink: 0, background: 'white', borderRadius: 16,
        border: '1px solid #EAECF0', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
      }}>
        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${accentColor}10 0%, ${accentColor}05 100%)`, padding: '16px 16px 14px', borderBottom: `1px solid ${accentColor}12` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: 'white', border: `1.5px solid ${accentColor}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, boxShadow: `0 2px 8px ${accentColor}12` }}>
              {getJobEmoji(interview.title)}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', lineHeight: 1.3, marginBottom: 4 }}>{interview.title}</div>
              <div style={{ display: 'flex', gap: 5 }}>
                <span style={{ fontSize: 9, fontWeight: 800, color: accentColor, background: `${accentColor}14`, border: `1px solid ${accentColor}28`, borderRadius: 4, padding: '1px 6px', textTransform: 'uppercase', letterSpacing: 0.3 }}>{seniority}</span>
                <span style={{ fontSize: 9, fontWeight: 600, color: '#9CA3AF', background: '#F3F4F6', borderRadius: 4, padding: '1px 6px' }}>{allQ.length} Qs</span>
              </div>
            </div>
          </div>
        </div>

        {/* Practice summary strip */}
        {practiced.length > 0 && (
          <div style={{ margin: '10px 12px 0', padding: '8px 12px', background: `${scoreColor(avgScore)}0D`, border: `1px solid ${scoreColor(avgScore)}22`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(avgScore), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <TrendingUp size={12} /> {avgScore}/10 avg
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280' }}>{practiced.length}/{allQ.length} done</span>
          </div>
        )}

        {/* Tabs */}
        <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 9, padding: 3, gap: 2 }}>
            {['interview', 'followup'].map(tab => (
              <button key={tab} type="button" onClick={() => setActiveTab(tab)}
                style={{ flex: 1, border: 'none', borderRadius: 7, background: activeTab === tab ? '#111827' : 'transparent', color: activeTab === tab ? 'white' : '#6B7280', padding: '7px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}>
                {tab === 'interview' ? 'Interview' : 'Follow Up'}
              </button>
            ))}
          </div>
        </div>

        {/* Question list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          {questions.length === 0 ? (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
              No {activeTab === 'interview' ? 'interview' : 'follow-up'} questions.
            </div>
          ) : questions.map((item, idx) => {
            const isActive = selectedQ === item._id || selectedQ === idx;
            return (
              <button key={item._id || idx} type="button"
                onClick={() => { setSelectedQ(item._id || idx); setShowAnswer(false); }}
                style={{ width: '100%', textAlign: 'left', border: 'none', background: isActive ? '#FFF1F2' : 'transparent', borderLeft: `3px solid ${isActive ? '#EF1D28' : 'transparent'}`, padding: '10px 14px 10px 13px', cursor: 'pointer', transition: 'background 0.12s', display: 'flex', alignItems: 'flex-start', gap: 9 }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#F9FAFB'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ minWidth: 20, height: 20, borderRadius: 6, background: isActive ? '#EF1D28' : '#F3F4F6', color: isActive ? 'white' : '#9CA3AF', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  {idx + 1}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: isActive ? '#111827' : '#374151', fontWeight: isActive ? 700 : 500, lineHeight: 1.45, marginBottom: item.practice?.score != null ? 4 : 0 }}>
                    {item.question.length > 72 ? item.question.slice(0, 72) + '…' : item.question}
                  </div>
                  {item.practice?.score != null && <ScoreBadge score={item.practice.score} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Right panel ── */}
      <div style={{ flex: 1, paddingLeft: 22, overflowY: 'auto' }}>
        {!found ? (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF' }}>
            {practiced.length > 0 && (
              <div style={{ background: 'white', border: '1px solid #EAECF0', borderRadius: 16, padding: '22px 26px', marginBottom: 24, minWidth: 280, maxWidth: 440, textAlign: 'left', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Session Summary</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 36, fontWeight: 900, color: scoreColor(avgScore), lineHeight: 1 }}>{avgScore}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#9CA3AF' }}>/10 average</span>
                </div>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: weakest.length ? 16 : 0 }}>{practiced.length} of {allQ.length} questions practiced</div>
                {weakest.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#D97706', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Weakest areas</div>
                    {weakest.map(item => (
                      <div key={item._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 12, color: '#374151', marginBottom: 6, padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                        <span>{item.question.length > 55 ? item.question.slice(0, 55) + '…' : item.question}</span>
                        <ScoreBadge score={item.practice.score} />
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
            <div style={{ fontSize: 15, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>← Select a question to practice</div>
            <div style={{ fontSize: 12, color: '#9CA3AF' }}>
              {interview.mode === 'personalized' ? 'Questions tailored to your profile.' : 'AI-generated questions for this role.'}
            </div>
          </div>
        ) : (
          <div>
            {/* Question card */}
            <div style={{ background: 'white', border: '1px solid #EAECF0', borderRadius: 14, overflow: 'hidden', marginBottom: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
              <div style={{ background: 'linear-gradient(135deg, #FFF1F2, #FFF8F6)', borderBottom: '1px solid #FEE2E2', padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#EF1D28', background: '#FFF1F2', border: '1px solid #FECACA', borderRadius: 4, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Question</span>
                <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 500 }}>
                  {questions.findIndex(q => (q._id || questions.indexOf(q)) === (found._id || questions.indexOf(found))) + 1} of {questions.length}
                </span>
              </div>
              <div style={{ padding: '16px 18px' }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#111827', lineHeight: 1.55, marginBottom: found.hint ? 12 : 0 }}>
                  {found.question}
                </div>
                {found.hint && (
                  <div style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 1.6, fontStyle: 'italic', borderTop: '1px solid #F3F4F6', paddingTop: 10 }}>
                    {found.hint}
                  </div>
                )}
              </div>
            </div>

            {/* Your Answer card */}
            <div style={{ background: 'white', border: '1px solid #EAECF0', borderRadius: 14, overflow: 'hidden', marginBottom: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
              <div style={{ background: 'linear-gradient(135deg, #F5F3FF, #FAF9FF)', borderBottom: '1px solid #EDE9FE', padding: '10px 18px' }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#7C3AED', background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 4, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Your Answer</span>
              </div>
              <div style={{ padding: '14px 16px' }}>
                <textarea
                  value={draftAnswer}
                  onChange={e => setDraftAnswer(e.target.value)}
                  placeholder="Type your answer here, then get AI feedback on how to improve…"
                  rows={5}
                  style={{ width: '100%', border: '1.5px solid #E5E7EB', borderRadius: 10, padding: '11px 14px', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.6, color: '#374151', background: '#FAFAFA', transition: 'border-color 0.15s' }}
                  onFocus={e => { e.target.style.borderColor = '#EF1D28'; e.target.style.background = 'white'; }}
                  onBlur={e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.background = '#FAFAFA'; }}
                />
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                  <button type="button" disabled={!draftAnswer.trim() || aiLoading || saving} onClick={handleGetFeedback}
                    style={{ flex: 1, background: !draftAnswer.trim() || aiLoading || saving ? '#F3F4F6' : 'linear-gradient(135deg,#EF1D28,#C5161D)', color: !draftAnswer.trim() || aiLoading || saving ? '#9CA3AF' : 'white', border: 'none', borderRadius: 10, padding: '11px 16px', fontSize: 13, fontWeight: 700, cursor: !draftAnswer.trim() || aiLoading || saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, transition: 'all 0.15s', boxShadow: !draftAnswer.trim() || aiLoading || saving ? 'none' : '0 3px 12px rgba(239,29,40,0.25)' }}>
                    {(aiLoading || saving) ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Sparkles size={15} />}
                    {(aiLoading || saving) ? 'Evaluating…' : found.practice?.score != null ? 'Re-evaluate with AI' : 'Get AI Feedback'}
                  </button>
                  <button type="button" onClick={() => setShowAnswer(v => !v)}
                    style={{ padding: '11px 16px', border: '1.5px solid #E5E7EB', background: showAnswer ? '#F9FAFB' : 'white', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', transition: 'all 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#D1D5DB'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#E5E7EB'}>
                    {showAnswer ? <EyeOff size={14} /> : <Eye size={14} />}
                    {showAnswer ? 'Hide Answer' : 'Show Answer'}
                  </button>
                </div>
              </div>
            </div>

            {/* Sample answer (revealed) */}
            {showAnswer && found.answer && (
              <div style={{ background: 'white', border: '1px solid #EAECF0', borderRadius: 14, overflow: 'hidden', marginBottom: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
                <div style={{ background: 'linear-gradient(135deg, #FFF7ED, #FFFBF5)', borderBottom: '1px solid #FED7AA', padding: '10px 18px' }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: '#EA580C', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 4, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Sample Answer</span>
                </div>
                <div style={{ padding: '16px 18px' }}>
                  <div style={{ fontSize: 14, color: '#1F2937', lineHeight: 1.7 }}>{renderBold(found.answer)}</div>
                  {found.answerHint && (
                    <div style={{ fontStyle: 'italic', color: '#9CA3AF', fontSize: 12, lineHeight: 1.6, borderTop: '1px solid #F3F4F6', paddingTop: 10, marginTop: 10 }}>{found.answerHint}</div>
                  )}
                </div>
              </div>
            )}

            {/* AI Feedback card */}
            {found.practice?.score != null && (
              <div style={{ background: 'white', border: '1px solid #EAECF0', borderRadius: 14, overflow: 'hidden', marginBottom: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
                <div style={{ background: `linear-gradient(135deg, ${scoreColor(found.practice.score)}10, ${scoreColor(found.practice.score)}05)`, borderBottom: `1px solid ${scoreColor(found.practice.score)}20`, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: '#111827', textTransform: 'uppercase', letterSpacing: 0.5 }}>AI Feedback</span>
                  <ScoreBadge score={found.practice.score} size="lg" />
                  {found.practice.practicedAt && (
                    <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 'auto' }}>
                      {new Date(found.practice.practicedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
                <div style={{ padding: '16px 18px' }}>
                  {(found.practice.strengths || []).length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: '#059669', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Strengths</div>
                      {found.practice.strengths.map((s, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: '#1F2937', lineHeight: 1.55, marginBottom: 6, alignItems: 'flex-start' }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#ECFDF5', border: '1px solid #A7F3D0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                            <CheckCircle2 size={11} color="#059669" />
                          </div>
                          <span>{s}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(found.practice.improvements || []).length > 0 && (
                    <div style={{ marginBottom: found.practice.suggestedAnswer ? 14 : 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: '#D97706', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Improvements</div>
                      {found.practice.improvements.map((s, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: '#1F2937', lineHeight: 1.55, marginBottom: 6, alignItems: 'flex-start' }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#FFFBEB', border: '1px solid #FDE68A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                            <TrendingUp size={10} color="#D97706" />
                          </div>
                          <span>{s}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {found.practice.suggestedAnswer && (
                    <>
                      <div style={{ fontSize: 10, fontWeight: 800, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Suggested Answer</div>
                      <div style={{ fontSize: 13, color: '#1F2937', lineHeight: 1.7, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 14px' }}>
                        {renderBold(found.practice.suggestedAnswer)}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {detailDailyLimit && <DailyLimitModal onClose={clearDetailDailyLimit} />}
    </div>
  );
};

// ─── Main export ──────────────────────────────────────────────────────────────

export const InterviewsPage = ({ docs = [], initialInterview, onInitialLoaded }) => {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { showConfirm } = useDialog();

  useEffect(() => {
    api.getInterviews()
      .then(setInterviews)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  /* Open a just-created interview from job search immediately */
  useEffect(() => {
    if (!initialInterview) return;
    setInterviews(prev => {
      const exists = prev.some(iv => iv._id === initialInterview._id);
      return exists ? prev : [initialInterview, ...prev];
    });
    setSelected(initialInterview);
    onInitialLoaded?.();
  }, [initialInterview]);

  const handleCreated = (interview) => {
    setInterviews(prev => [interview, ...prev]);
    setShowModal(false);
    setSelected(interview);
  };

  const handleUpdated = (updated) => {
    setSelected(updated);
    setInterviews(prev => prev.map(iv => iv._id === updated._id ? updated : iv));
  };

  const handleDelete = async (id) => {
    const ok = await showConfirm('Are you sure you want to delete this interview? This cannot be undone.', {
      title: 'Delete Interview', confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    await api.deleteInterview(id);
    setInterviews(prev => prev.filter(iv => iv._id !== id));
    if (selected?._id === id) setSelected(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <RocketLoader size={160} message="Loading interviews…" />
      </div>
    );
  }

  return (
    <div style={{ height: '100%' }}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @media (max-width: 760px) {
          .interview-detail-split { flex-direction: column !important; height: auto !important; }
          .interview-detail-sidebar { width: 100% !important; max-height: 260px !important; border-right: none !important; border-bottom: 1px solid #E5E7EB !important; border-radius: 14px 14px 0 0 !important; }
        }
        @media (max-width: 540px) {
          .interview-cards-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 680px) {
          .interview-mode-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: selected ? 20 : 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {selected && (
            <button
              type="button"
              onClick={() => setSelected(null)}
              style={{ border: '1.5px solid #E5E7EB', background: 'white', cursor: 'pointer', color: '#6B7280', display: 'flex', alignItems: 'center', padding: '7px', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'all 0.15s' }}
              title="Back to list"
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#D1D5DB'; e.currentTarget.style.color = '#374151'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.color = '#6B7280'; }}
            >
              <ArrowLeft size={17} />
            </button>
          )}
          <div style={{ width: 38, height: 38, borderRadius: 11, background: 'linear-gradient(135deg,#FB923C,#EF1D28)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 10px rgba(239,29,40,0.25)', flexShrink: 0 }}>
            <Sparkles size={17} color="white" strokeWidth={2.2} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827', letterSpacing: -0.5 }}>Job Interviews</h1>
            {!selected && <p style={{ margin: 0, fontSize: 12, color: '#9CA3AF', marginTop: 1 }}>{interviews.length} session{interviews.length !== 1 ? 's' : ''} · AI-powered practice</p>}
          </div>
        </div>
        {!selected && (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            style={{ border: 'none', background: 'linear-gradient(135deg,#EF1D28,#C5161D)', color: 'white', borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 4px 14px rgba(239,29,40,0.28)', fontFamily: 'Inter,sans-serif', letterSpacing: 0.1 }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 6px 20px rgba(239,29,40,0.38)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 4px 14px rgba(239,29,40,0.28)'}
          >
            <Plus size={15} strokeWidth={2.5} /> Create New
          </button>
        )}
      </div>

      {/* List view */}
      {!selected && (
        <>
          {/* Stats strip */}
          {interviews.length > 0 && (() => {
            const allPracticed = interviews.flatMap(iv => (iv.questions || []).filter(q => q.practice?.score != null));
            const globalAvg = allPracticed.length
              ? Math.round(allPracticed.reduce((s, q) => s + q.practice.score, 0) / allPracticed.length * 10) / 10
              : null;
            const videoCount = interviews.filter(iv => iv.mode === 'video').length;
            return (
              <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
                {[
                  { label: 'Sessions', value: interviews.length, color: '#EF1D28' },
                  { label: 'Practiced', value: allPracticed.length, color: '#7C3AED' },
                  { label: 'Avg Score', value: globalAvg != null ? `${globalAvg}/10` : '—', color: globalAvg != null ? scoreColor(globalAvg) : '#9CA3AF' },
                  { label: 'Video', value: videoCount, color: '#0891B2' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ flex: 1, background: 'white', border: '1px solid #EAECF0', borderRadius: 12, padding: '10px 14px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3, fontWeight: 500 }}>{label}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Choose a practice mode — both interview types shown with equal weight */}
          <div style={{ fontSize: 12, fontWeight: 800, color: '#9CA3AF', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 12 }}>
            Choose a Practice Mode
          </div>
          <div className="interview-mode-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>

            {/* Video interview card */}
            <div
              onClick={() => navigate('/video-interview')}
              style={{ background: 'linear-gradient(135deg, #FFF5F5 0%, #FFFFFF 55%)', border: '1.5px solid #FECACA', borderRadius: 18, padding: '22px 24px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 14px rgba(239,29,40,0.07)', display: 'flex', flexDirection: 'column' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(239,29,40,0.16)'; e.currentTarget.style.borderColor = '#EF1D28'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 14px rgba(239,29,40,0.07)'; e.currentTarget.style.borderColor = '#FECACA'; }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ width: 50, height: 50, borderRadius: 14, background: 'linear-gradient(135deg,#EF1D28,#F97316)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(239,29,40,0.28)' }}>
                  <Video size={22} color="white" />
                </div>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#0891B2', background: '#ECFEFF', border: '1px solid #A5F3FC', borderRadius: 20, padding: '3px 10px', letterSpacing: 0.3 }}>LIVE</span>
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#111827', marginBottom: 6, letterSpacing: -0.2 }}>AI Video Interview</div>
              <div style={{ fontSize: 12.5, color: '#6B7280', lineHeight: 1.6, marginBottom: 16 }}>
                Answer on camera with real-time speech recognition. The AI interviewer asks adaptive follow-ups and scores you like a real hiring manager.
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
                {['Live Camera', 'Speech AI', 'Full Report'].map(label => (
                  <span key={label} style={{ fontSize: 10, fontWeight: 700, color: '#C5161D', background: '#FFF1F2', border: '1px solid #FECACA', borderRadius: 5, padding: '3px 9px' }}>{label}</span>
                ))}
              </div>
              <button
                onClick={e => e.stopPropagation()}
                style={{ marginTop: 'auto', background: 'linear-gradient(135deg,#EF1D28,#C5161D)', border: 'none', borderRadius: 10, padding: '11px 0', fontSize: 13, fontWeight: 700, color: 'white', cursor: 'pointer', boxShadow: '0 4px 14px rgba(239,29,40,0.28)', pointerEvents: 'none' }}
              >
                Start Video Interview →
              </button>
            </div>

            {/* Text-based practice card */}
            <div
              onClick={() => setShowModal(true)}
              style={{ background: 'linear-gradient(135deg, #F5F3FF 0%, #FFFFFF 55%)', border: '1.5px solid #DDD6FE', borderRadius: 18, padding: '22px 24px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 14px rgba(124,58,237,0.07)', display: 'flex', flexDirection: 'column' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(124,58,237,0.16)'; e.currentTarget.style.borderColor = '#7C3AED'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 14px rgba(124,58,237,0.07)'; e.currentTarget.style.borderColor = '#DDD6FE'; }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ width: 50, height: 50, borderRadius: 14, background: 'linear-gradient(135deg,#7C3AED,#6366F1)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(124,58,237,0.28)' }}>
                  <FileText size={22} color="white" />
                </div>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#EA580C', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 20, padding: '3px 10px', letterSpacing: 0.3 }}>AI</span>
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#111827', marginBottom: 6, letterSpacing: -0.2 }}>Text-Based Practice</div>
              <div style={{ fontSize: 12.5, color: '#6B7280', lineHeight: 1.6, marginBottom: 16 }}>
                Generate a tailored question set — Basic or Personalized to your resume and a job ad — then write answers and get instant AI feedback.
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
                {['Basic & Personalized', 'Written Practice', 'AI Feedback'].map(label => (
                  <span key={label} style={{ fontSize: 10, fontWeight: 700, color: '#6D28D9', background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 5, padding: '3px 9px' }}>{label}</span>
                ))}
              </div>
              <button
                onClick={e => e.stopPropagation()}
                style={{ marginTop: 'auto', background: 'linear-gradient(135deg,#7C3AED,#6366F1)', border: 'none', borderRadius: 10, padding: '11px 0', fontSize: 13, fontWeight: 700, color: 'white', cursor: 'pointer', boxShadow: '0 4px 14px rgba(124,58,237,0.28)', pointerEvents: 'none' }}
              >
                Create Interview →
              </button>
            </div>
          </div>

          {/* How AI Video Interview Prep works — condensed, light theme */}
          <div style={{ background: 'white', border: '1px solid #EAECF0', borderRadius: 16, padding: '20px 24px', marginBottom: 24, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 16 }}>How AI Video Interview Prep Works</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 18 }}>
              {[
                { step: '01', title: 'Set Your Role', desc: 'Enter your target job title and paste the job description for tailored questions.' },
                { step: '02', title: 'Live Interview', desc: 'An AI interviewer speaks questions aloud. You answer on camera with real-time speech recognition.' },
                { step: '03', title: 'Adaptive AI', desc: 'Claude analyzes each answer and dynamically generates follow-up or clarifying questions.' },
                { step: '04', title: 'Full Report', desc: 'Get a detailed score, hiring likelihood, strengths, improvement areas, and communication metrics.' },
              ].map(({ step, title, desc }) => (
                <div key={step} style={{ background: '#F8F9FB', border: '1px solid #EAECF0', borderRadius: 12, padding: '14px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#EF1D28', letterSpacing: 0.5, marginBottom: 6 }}>STEP {step}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 5 }}>{title}</div>
                  <div style={{ fontSize: 11, color: '#6B7280', lineHeight: 1.55 }}>{desc}</div>
                </div>
              ))}
            </div>

            <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 10 }}>What's included in every session</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[
                  { icon: '🎙️', text: 'Real-time speech transcription' },
                  { icon: '🤖', text: 'Adaptive AI questioning (Claude)' },
                  { icon: '📊', text: 'Filler word & pace tracking' },
                  { icon: '⚡', text: 'Per-question scoring (0–10)' },
                  { icon: '🏆', text: 'Hiring likelihood rating' },
                  { icon: '💬', text: 'Strengths & improvement tips' },
                  { icon: '⏱️', text: '5–7 questions · 10–15 min' },
                  { icon: '🎯', text: 'Technical, behavioral & confidence scores' },
                ].map(({ icon, text }) => (
                  <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F8F9FB', border: '1px solid #EAECF0', borderRadius: 8, padding: '5px 10px' }}>
                    <span style={{ fontSize: 13 }}>{icon}</span>
                    <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 500 }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {interviews.length > 0 && (() => {
            const videoSessions = interviews.filter(iv => iv.mode === 'video');
            const textSessions  = interviews.filter(iv => iv.mode !== 'video');

            const renderGroup = (label, color, list) => list.length === 0 ? null : (
              <div key={label} style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color, letterSpacing: 0.8, textTransform: 'uppercase' }}>{label}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', background: '#F3F4F6', borderRadius: 10, padding: '1px 8px' }}>{list.length}</span>
                </div>
                <div className="interview-cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16 }}>
                  {list.map(iv => (
                    <InterviewCard
                      key={iv._id}
                      interview={iv}
                      onClick={() => setSelected(iv)}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            );

            return (
              <>
                {renderGroup('Video Interviews', '#EF1D28', videoSessions)}
                {renderGroup('Text-Based Practice', '#7C3AED', textSessions)}
              </>
            );
          })()}
          {interviews.length === 0 && (
            <div style={{ marginTop: 48, textAlign: 'center', color: '#9CA3AF' }}>
              <div style={{ fontSize: 48, marginBottom: 14 }}>🎯</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 6 }}>No interviews yet</div>
              <div style={{ fontSize: 13, marginBottom: 20, color: '#9CA3AF' }}>Create your first AI-powered practice session</div>
              <button
                type="button"
                onClick={() => setShowModal(true)}
                style={{ background: 'linear-gradient(135deg,#EF1D28,#FB923C)', color: 'white', border: 'none', borderRadius: 10, padding: '11px 24px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Plus size={14} />New Interview
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail view */}
      {selected && (
        selected.mode === 'video'
          ? <VideoReportView interview={selected} />
          : <DetailView interview={selected} onBack={() => setSelected(null)} onUpdate={handleUpdated} />
      )}

      {/* Create modal */}
      {showModal && (
        <CreateModal
          onClose={() => setShowModal(false)}
          onCreated={handleCreated}
          docs={docs}
        />
      )}
    </div>
  );
};
